import React, { useEffect, useState } from "react";
import {
  Terminal,
  Loader,
  AlertCircle,
  Maximize2,
  Minimize2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { LLMConversationHistory, LLMConversationEntry } from "../types";
import { useWebSocket } from "../hooks/useWebSocket";

// Get API base URL (matches ApiService configuration)
const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    return `${apiUrl}/api`;
  } else if (process.env.NODE_ENV === "production") {
    return "https://api.roboworks.com/api";
  } else {
    return "http://localhost:8000/api";
  }
};

interface LLMConversationTerminalProps {
  workflowId: string;
  className?: string;
}

// Phase 8: Group by ticket instead of agent (SWRM-47)
// SWRM-96: taskId can be null for "[No Active Task]" groups
interface TaskConversation {
  taskId: string | null;
  taskTitle: string;
  entries: LLMConversationEntry[];
  isExpanded: boolean;
}

interface TicketConversation {
  ticketKey: string;
  ticketId: number | null;
  nonTaskEntries: LLMConversationEntry[]; // Task generation prompts/responses
  tasks: TaskConversation[]; // Individual task conversations
  isExpanded: boolean;
  isTaskGenerationExpanded: boolean; // For collapsing Task Generation section
}

// Utility function to unescape text content from LLM responses
const unescapeText = (text: string): string => {
  if (!text) return text;

  try {
    // Replace escaped characters with their actual values
    return text
      .replace(/\\n/g, "\n")
      .replace(/\\r/g, "\r")
      .replace(/\\t/g, "\t")
      .replace(/\\'/g, "'")
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, "\\");
  } catch (error) {
    console.error("Error unescaping text:", error);
    return text;
  }
};

export const LLMConversationTerminal: React.FC<
  LLMConversationTerminalProps
> = ({ workflowId, className = "" }) => {
  const [ticketConversations, setTicketConversations] = useState<
    TicketConversation[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(
    new Set()
  );
  const { lastMessage } = useWebSocket();

  // Toggle individual message expansion
  const toggleMessageExpansion = (entryId: number, section: string) => {
    const key = `${entryId}-${section}`;
    setExpandedMessages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const isMessageExpanded = (entryId: number, section: string) => {
    return expandedMessages.has(`${entryId}-${section}`);
  };

  // SWRM-96: Group entries by ticket and task, maintaining chronological order
  // Groups consecutive messages with same task_id into task blocks
  const groupEntriesByTicket = React.useCallback(
    (entries: LLMConversationEntry[]): TicketConversation[] => {
      const ticketMap = new Map<string, TicketConversation>();

      entries.forEach((entry) => {
        const ticketKey = entry.ticket_key || entry.agent_name || "Other";
        const ticketId = entry.ticket_id || null;
        const taskId = entry.task_id || null;

        if (!ticketMap.has(ticketKey)) {
          ticketMap.set(ticketKey, {
            ticketKey,
            ticketId,
            nonTaskEntries: [],
            tasks: [],
            isExpanded: false,
            isTaskGenerationExpanded: false,
          });
        }

        const ticketConv = ticketMap.get(ticketKey)!;

        // Special handling for task_generation (always goes to nonTaskEntries for backward compat)
        if (taskId === "task_generation") {
          ticketConv.nonTaskEntries.push(entry);
        } else {
          // For all other entries, we'll reorganize them chronologically later
          // For now, just collect them
          if (taskId) {
            let taskConv = ticketConv.tasks.find((t) => t.taskId === taskId);
            if (!taskConv) {
              taskConv = {
                taskId,
                taskTitle: entry.task_title || `Task ${taskId}`, // Use backend-provided title or fallback
                entries: [],
                isExpanded: false,
              };
              ticketConv.tasks.push(taskConv);
            }
            taskConv.entries.push(entry);
          } else {
            ticketConv.nonTaskEntries.push(entry);
          }
        }
      });

      // SWRM-96: Reorganize entries chronologically with task groups
      ticketMap.forEach((ticketConv) => {
        // Check if there are any task groups (excluding task_generation)
        const hasTaskGroups = ticketConv.tasks.length > 0;

        if (hasTaskGroups) {
          // Flatten all entries with timestamps for chronological sorting
          const allEntriesWithType: Array<{
            entry: LLMConversationEntry;
            taskId: string | null;
            isTaskGeneration: boolean;
          }> = [];

          // Add non-task entries (excluding task_generation which is handled separately)
          const taskGenerationEntries = ticketConv.nonTaskEntries.filter(
            (e) => e.task_id === "task_generation"
          );
          const regularNonTaskEntries = ticketConv.nonTaskEntries.filter(
            (e) => e.task_id !== "task_generation"
          );

          regularNonTaskEntries.forEach((entry) => {
            allEntriesWithType.push({
              entry,
              taskId: null,
              isTaskGeneration: false,
            });
          });

          // Add task entries
          ticketConv.tasks.forEach((task) => {
            task.entries.forEach((entry) => {
              allEntriesWithType.push({
                entry,
                taskId: task.taskId,
                isTaskGeneration: false,
              });
            });
          });

          // Sort all entries by timestamp
          allEntriesWithType.sort(
            (a, b) =>
              new Date(a.entry.timestamp).getTime() -
              new Date(b.entry.timestamp).getTime()
          );

          // Rebuild tasks array by grouping consecutive entries with same taskId
          const newTasks: typeof ticketConv.tasks = [];
          let currentGroup: (typeof ticketConv.tasks)[0] | null = null;
          let noActiveTaskCounter = 1;

          allEntriesWithType.forEach((item) => {
            if (item.taskId === null) {
              // This is a non-task entry
              if (currentGroup && currentGroup.taskId === null) {
                // Continue the current [No Active Task] group
                currentGroup.entries.push(item.entry);
              } else {
                // Start a new [No Active Task] group
                if (currentGroup) {
                  newTasks.push(currentGroup);
                }
                currentGroup = {
                  taskId: null,
                  taskTitle: `[No Active Task ${noActiveTaskCounter}]`,
                  entries: [item.entry],
                  isExpanded: false,
                };
                noActiveTaskCounter++;
              }
            } else {
              // This is a task entry
              if (currentGroup && currentGroup.taskId === item.taskId) {
                // Continue the current task group
                currentGroup.entries.push(item.entry);
              } else {
                // Start a new task group
                if (currentGroup) {
                  newTasks.push(currentGroup);
                }
                currentGroup = {
                  taskId: item.taskId,
                  taskTitle: item.entry.task_title || `Task ${item.taskId}`, // Use backend-provided title
                  entries: [item.entry],
                  isExpanded: false,
                };
              }
            }
          });

          // Don't forget the last group
          if (currentGroup) {
            newTasks.push(currentGroup);
          }

          // Replace the tasks array with chronologically ordered groups
          ticketConv.tasks = newTasks;
          // Keep only task_generation in nonTaskEntries
          ticketConv.nonTaskEntries = taskGenerationEntries;
        }
        // If no task groups, leave as-is for backward compatibility
      });

      return Array.from(ticketMap.values());
    },
    []
  );

  // Fetch initial conversation history
  useEffect(() => {
    const fetchConversation = async () => {
      try {
        setLoading(true);

        // Get auth token from localStorage
        const token =
          localStorage.getItem("auth_token") || localStorage.getItem("token");

        const baseUrl = getApiBaseUrl();
        const response = await fetch(
          `${baseUrl}/workflows/${workflowId}/llm-conversation/current`,
          {
            headers: {
              "Content-Type": "application/json",
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch conversation");
        }

        const data: LLMConversationHistory = await response.json();
        const grouped = groupEntriesByTicket(data.entries);
        setTicketConversations(grouped);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
        console.error("Error fetching LLM conversation:", err);
      } finally {
        setLoading(false);
      }
    };

    if (workflowId) {
      fetchConversation();
    }
  }, [workflowId, groupEntriesByTicket]);

  // Handle WebSocket updates
  useEffect(() => {
    if (!lastMessage) return;

    const message = lastMessage;

    // Check if this is an LLM monitoring message for our workflow
    if (
      message.type === "llm_monitoring" &&
      message.data?.workflow_id === parseInt(workflowId)
    ) {
      // Create entry from WebSocket data, using timestamp as fallback ID if needed
      console.log("[DEBUG] WebSocket entry:", {
        id: message.data.id,
        ticket_id: message.data.ticket_id,
        ticket_key: message.data.ticket_key,
        agent_name: message.data.agent_name,
      });
      const newEntry: LLMConversationEntry = {
        id: message.data.id || Date.now(),
        workflow_id: message.data.workflow_id,
        ticket_id: message.data.ticket_id,
        ticket_key: message.data.ticket_key,
        task_id: message.data.task_id, // SWRM-102: Use task_id from backend
        task_title: message.data.task_title, // Task title for display
        agent_name: message.data.agent_name,
        agent_type: message.data.agent_type,
        message_type: message.data.message_type,
        timestamp: message.data.timestamp,
        messages: message.data.content?.messages,
        prompt: message.data.content?.prompt,
        system_prompt: message.data.content?.system_prompt,
        response_content: message.data.content?.response_content,
        model: message.data.content?.model,
        usage_data: message.data.content?.usage_data,
        duration_ms: message.data.content?.duration_ms,
      };

      setTicketConversations((prev) => {
        // Deduplication: Check if entry with this ID already exists
        const entryExists = prev.some(
          (tc) =>
            tc.nonTaskEntries.some((e) => e.id === newEntry.id) ||
            tc.tasks.some((t) => t.entries.some((e) => e.id === newEntry.id))
        );

        if (entryExists) {
          console.log(
            `[DEBUG] Skipping duplicate WebSocket entry: ${newEntry.id} (${newEntry.agent_name})`
          );
          return prev; // Don't add duplicate
        }

        const ticketKey = newEntry.ticket_key || newEntry.agent_name || "Other";
        const taskId = newEntry.task_id || null;

        const existingTicketIndex = prev.findIndex(
          (tc) => tc.ticketKey === ticketKey
        );

        if (existingTicketIndex >= 0) {
          // Add entry to existing ticket
          const updated = [...prev];
          const existingTicket = updated[existingTicketIndex];

          // Special handling for task_generation
          if (taskId === "task_generation") {
            updated[existingTicketIndex] = {
              ...existingTicket,
              nonTaskEntries: [...existingTicket.nonTaskEntries, newEntry],
              isExpanded: true,
              isTaskGenerationExpanded: true,
            };
          } else if (taskId) {
            // This is a task-specific entry (T001, T002, etc.)
            const existingTaskIndex = existingTicket.tasks.findIndex(
              (t) => t.taskId === taskId
            );

            if (existingTaskIndex >= 0) {
              // Add to existing task
              const updatedTasks = [...existingTicket.tasks];
              updatedTasks[existingTaskIndex] = {
                ...updatedTasks[existingTaskIndex],
                entries: [...updatedTasks[existingTaskIndex].entries, newEntry],
                isExpanded: true,
              };
              updated[existingTicketIndex] = {
                ...existingTicket,
                tasks: updatedTasks,
                isExpanded: true,
              };
            } else {
              // Create new task - append to end (chronological order)
              updated[existingTicketIndex] = {
                ...existingTicket,
                tasks: [
                  ...existingTicket.tasks,
                  {
                    taskId,
                    taskTitle: `Task ${taskId}`,
                    entries: [newEntry],
                    isExpanded: true,
                  },
                ],
                isExpanded: true,
              };
            }
          } else {
            // SWRM-96: No active task - check if we should create/append to [No Active Task] group
            if (existingTicket.tasks.length > 0) {
              // There are task groups, so we need [No Active Task] groups
              const lastTask =
                existingTicket.tasks[existingTicket.tasks.length - 1];

              if (lastTask.taskId === null) {
                // Last group is already [No Active Task], append to it
                const updatedTasks = [...existingTicket.tasks];
                updatedTasks[updatedTasks.length - 1] = {
                  ...lastTask,
                  entries: [...lastTask.entries, newEntry],
                  isExpanded: true,
                };
                updated[existingTicketIndex] = {
                  ...existingTicket,
                  tasks: updatedTasks,
                  isExpanded: true,
                };
              } else {
                // Last group is a task, create new [No Active Task] group
                const noActiveTaskCount = existingTicket.tasks.filter(
                  (t) => t.taskId === null
                ).length;
                updated[existingTicketIndex] = {
                  ...existingTicket,
                  tasks: [
                    ...existingTicket.tasks,
                    {
                      taskId: null,
                      taskTitle: `[No Active Task ${noActiveTaskCount + 1}]`,
                      entries: [newEntry],
                      isExpanded: true,
                    },
                  ],
                  isExpanded: true,
                };
              }
            } else {
              // No task groups exist yet, add to nonTaskEntries for backward compat
              updated[existingTicketIndex] = {
                ...existingTicket,
                nonTaskEntries: [...existingTicket.nonTaskEntries, newEntry],
                isExpanded: true,
                isTaskGenerationExpanded: true,
              };
            }
          }
          return updated;
        } else {
          // Create new ticket conversation
          const isTaskGeneration = taskId === "task_generation";
          return [
            ...prev,
            {
              ticketKey,
              ticketId: newEntry.ticket_id || null,
              nonTaskEntries: isTaskGeneration || !taskId ? [newEntry] : [],
              tasks:
                taskId && !isTaskGeneration
                  ? [
                      {
                        taskId,
                        taskTitle: `Task ${taskId}`,
                        entries: [newEntry],
                        isExpanded: true,
                      },
                    ]
                  : [],
              isExpanded: true,
              isTaskGenerationExpanded: isTaskGeneration || !taskId,
            },
          ];
        }
      });
    }
  }, [lastMessage, workflowId]); // No more dependencies on parsing functions

  // Phase 8: Toggle ticket block expansion
  const toggleTicketExpansion = (ticketKey: string) => {
    setTicketConversations((prev) =>
      prev.map((tc) =>
        tc.ticketKey === ticketKey ? { ...tc, isExpanded: !tc.isExpanded } : tc
      )
    );
  };

  // Toggle task block expansion within a ticket
  const toggleTaskExpansion = (ticketKey: string, taskKey: string) => {
    setTicketConversations((prev) =>
      prev.map((tc) =>
        tc.ticketKey === ticketKey
          ? {
              ...tc,
              tasks: tc.tasks.map((task) => {
                // Match using the same key logic: taskId OR taskTitle (for [No Active Task])
                const currentTaskKey = task.taskId || task.taskTitle;
                return currentTaskKey === taskKey
                  ? { ...task, isExpanded: !task.isExpanded }
                  : task;
              }),
            }
          : tc
      )
    );
  };

  // Toggle Task Generation block expansion within a ticket
  const toggleTaskGenerationExpansion = (ticketKey: string) => {
    setTicketConversations((prev) =>
      prev.map((tc) =>
        tc.ticketKey === ticketKey
          ? { ...tc, isTaskGenerationExpanded: !tc.isTaskGenerationExpanded }
          : tc
      )
    );
  };

  // Check if a key is an actual ticket (e.g., "SWRM-1", "HLO2-1") vs an agent name
  const isActualTicket = (ticketKey: string): boolean => {
    // Match pattern: letters/numbers, dash, numbers (e.g., SWRM-1, HLO2-1, MYTASK2-1)
    return /^[A-Z0-9]+-\d+$/.test(ticketKey);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
  };

  const renderEntry = (entry: LLMConversationEntry) => {
    const isRequest = entry.message_type === "request";
    const isToolResponse = entry.message_type === "tool_response";
    const isError = entry.message_type === "error";
    const isHumanInput = entry.message_type === "human_input"; // SWRM-111
    const timestamp = formatTimestamp(entry.timestamp);

    // Determine styling based on message type
    let bgClass, borderClass, textClass, label;
    if (isHumanInput) {
      // Human input gets distinct purple/indigo styling (SWRM-111)
      bgClass = "bg-indigo-50 dark:bg-indigo-900/20";
      borderClass = "border-l-4 border-indigo-500";
      textClass = "text-indigo-600 dark:text-indigo-400";
      label = "👤 HUMAN INPUT";
    } else if (isRequest) {
      bgClass = "bg-blue-50 dark:bg-blue-900/20";
      borderClass = "border-l-4 border-blue-500";
      textClass = "text-blue-600 dark:text-blue-400";
      label = "➤ REQUEST";
    } else if (isToolResponse) {
      bgClass = "bg-amber-50 dark:bg-amber-900/20";
      borderClass = "border-l-4 border-amber-500";
      textClass = "text-amber-600 dark:text-amber-400";
      label = "🔧 TOOL RESULT";
    } else if (isError) {
      bgClass = "bg-red-50 dark:bg-red-900/20";
      borderClass = "border-l-4 border-red-500";
      textClass = "text-red-600 dark:text-red-400";
      label = "❌ ERROR";
    } else {
      bgClass = "bg-green-50 dark:bg-green-900/20";
      borderClass = "border-l-4 border-green-500";
      textClass = "text-green-600 dark:text-green-400";
      label = "◀ RESPONSE";
    }

    return (
      <div
        key={entry.id}
        className={`mb-4 p-3 rounded ${bgClass} ${borderClass}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className={`text-xs font-mono ${textClass}`}>{label}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {timestamp}
            </span>
            {entry.duration_ms && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ({entry.duration_ms.toFixed(0)}ms)
              </span>
            )}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400">
            {entry.agent_name}
          </div>
        </div>

        {/* System Prompt (requests only) */}
        {isRequest && entry.system_prompt && (
          <div className="mb-2">
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
              System:
            </div>
            <div className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-white/50 dark:bg-black/20 p-2 rounded">
              {(() => {
                const unescapedSystem = unescapeText(entry.system_prompt);
                return unescapedSystem.length > 200 &&
                  !isMessageExpanded(entry.id, "system")
                  ? `${unescapedSystem.substring(0, 200)}...`
                  : unescapedSystem;
              })()}
            </div>
            {entry.system_prompt.length > 200 && (
              <button
                onClick={() => toggleMessageExpansion(entry.id, "system")}
                className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 mt-1"
              >
                {isMessageExpanded(entry.id, "system")
                  ? "▲ Show less"
                  : "▼ Show more"}
              </button>
            )}
          </div>
        )}

        {/* Prompt (requests) or Response Content */}
        <div>
          {isRequest ? (
            <>
              <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                Prompt:
              </div>
              <div className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-white/50 dark:bg-black/20 p-2 rounded">
                {(() => {
                  const unescapedPrompt = unescapeText(entry.prompt || "");
                  return unescapedPrompt &&
                    unescapedPrompt.length > 300 &&
                    !isMessageExpanded(entry.id, "prompt")
                    ? `${unescapedPrompt.substring(0, 300)}...`
                    : unescapedPrompt;
                })()}
              </div>
              {entry.prompt && entry.prompt.length > 300 && (
                <button
                  onClick={() => toggleMessageExpansion(entry.id, "prompt")}
                  className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 mt-1"
                >
                  {isMessageExpanded(entry.id, "prompt")
                    ? "▲ Show less"
                    : "▼ Show more"}
                </button>
              )}
            </>
          ) : (
            <>
              {/* Try to parse tool call data from response_content */}
              {(() => {
                let toolCallData = null;
                let isToolCall = false;
                let textContent = null;
                let displayContent = entry.response_content;

                // Check if response_content is a tool call JSON
                if (
                  entry.response_content &&
                  entry.response_content.trim().startsWith("{")
                ) {
                  try {
                    const parsed = JSON.parse(entry.response_content);
                    if (parsed.tool_call) {
                      toolCallData = parsed.tool_call;
                      isToolCall = true;
                      // Check if there's also text content (from LLM message before tool call)
                      textContent = parsed.text_content
                        ? unescapeText(parsed.text_content)
                        : null;
                    } else {
                      // Not a tool call, but might still need unescaping
                      displayContent = unescapeText(entry.response_content);
                    }
                  } catch {
                    // Not JSON or not a tool call, unescape as plain text
                    displayContent = unescapeText(entry.response_content || "");
                  }
                } else {
                  // Plain text response, unescape it
                  displayContent = unescapeText(entry.response_content || "");
                }

                if (isToolCall && toolCallData) {
                  // Render both text content (if present) and tool call
                  return (
                    <>
                      {/* Show agent's message if present */}
                      {textContent && (
                        <div className="mb-3">
                          <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                            Message:
                          </div>
                          <div className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-white/50 dark:bg-black/20 p-2 rounded">
                            {textContent}
                          </div>
                        </div>
                      )}

                      {/* Tool call */}
                      <div className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-1">
                        📞 Tool Call:
                      </div>
                      <div className="text-xs font-mono bg-purple-50 dark:bg-purple-900/20 p-3 rounded border border-purple-300 dark:border-purple-700">
                        <div className="font-semibold text-purple-700 dark:text-purple-300 mb-2">
                          {toolCallData.name}
                        </div>
                        <div className="text-gray-700 dark:text-gray-300 space-y-1">
                          {Object.entries(toolCallData.arguments || {}).map(
                            ([key, value]) => {
                              const valueStr =
                                typeof value === "string"
                                  ? value
                                  : JSON.stringify(value, null, 2);
                              
                              // SWRM-169: Check if this is an artifact reference
                              const isArtifactReference = 
                                typeof value === "string" && 
                                value.startsWith("@artifact:");
                              
                              // Parse artifact reference for display
                              let artifactInfo = null;
                              if (isArtifactReference) {
                                const match = valueStr.match(/^@artifact:([^:]+):(.+)$/);
                                if (match) {
                                  artifactInfo = {
                                    stepId: match[1],
                                    key: match[2],
                                  };
                                }
                              }
                              
                              const isTruncated =
                                valueStr.length > 200 &&
                                !isMessageExpanded(entry.id, `arg-${key}`);

                              return (
                                <div key={key} className="mb-2">
                                  <span className="font-semibold">{key}:</span>
                                  {isArtifactReference && artifactInfo ? (
                                    // SWRM-169: Render artifact reference with special styling
                                    <div className="ml-2 flex items-start gap-2 bg-blue-50 dark:bg-blue-900/20 p-2 rounded border border-blue-300 dark:border-blue-700">
                                      <span className="text-blue-600 dark:text-blue-400 flex-shrink-0">
                                        🔗
                                      </span>
                                      <div className="flex-1">
                                        <div className="text-xs font-mono text-blue-700 dark:text-blue-300">
                                          {valueStr}
                                        </div>
                                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                          Reference to artifact from step '{artifactInfo.stepId}' with key '{artifactInfo.key}'
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    // Regular parameter display
                                    <>
                                      <div className="ml-2 whitespace-pre-wrap bg-white/50 dark:bg-black/20 p-1 rounded">
                                        {isTruncated
                                          ? `${valueStr.substring(0, 200)}...`
                                          : valueStr}
                                      </div>
                                      {valueStr.length > 200 && (
                                        <button
                                          onClick={() =>
                                            toggleMessageExpansion(
                                              entry.id,
                                              `arg-${key}`
                                            )
                                          }
                                          className="text-xs text-purple-500 hover:text-purple-600 dark:text-purple-400 dark:hover:text-purple-300 mt-1"
                                        >
                                          {isMessageExpanded(entry.id, `arg-${key}`)
                                            ? "▲ Show less"
                                            : "▼ Show more"}
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            }
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          ID: {toolCallData.id}
                        </div>
                      </div>
                    </>
                  );
                }

                // Regular response or error rendering
                return (
                  <>
                    <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                      {isError
                        ? "Error:"
                        : isToolResponse
                        ? `Tool: ${entry.model || "Unknown"}`
                        : "Response:"}
                    </div>
                    <div className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-white/50 dark:bg-black/20 p-2 rounded">
                      {displayContent &&
                      displayContent.length > 500 &&
                      !isMessageExpanded(entry.id, "response")
                        ? `${displayContent.substring(0, 500)}...`
                        : displayContent}
                    </div>
                    {displayContent && displayContent.length > 500 && (
                      <button
                        onClick={() =>
                          toggleMessageExpansion(entry.id, "response")
                        }
                        className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 mt-1"
                      >
                        {isMessageExpanded(entry.id, "response")
                          ? "▲ Show less"
                          : "▼ Show more"}
                      </button>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>

        {/* Model Info (responses and tool_response only) */}
        {!isRequest && entry.model && !isToolResponse && (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Model: {entry.model}
            {entry.usage_data && (
              <span className="ml-2">
                | Tokens: {JSON.stringify(entry.usage_data)}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`bg-gray-900 text-gray-100 rounded-lg shadow-lg ${
        isExpanded ? "fixed inset-0 z-50 flex flex-col" : "h-full flex flex-col"
      } ${className}`}
    >
      {/* Terminal Header */}
      <div
        className={`flex items-center justify-between px-4 py-2 bg-gray-800 ${
          isExpanded ? "" : "rounded-t-lg"
        } border-b border-gray-700`}
      >
        <div className="flex items-center space-x-2">
          <Terminal className="h-4 w-4 text-green-400" />
          <span className="text-sm font-semibold">
            LLM Conversation Monitor
          </span>
          {ticketConversations.length > 0 && (
            <span className="text-xs text-gray-400">
              ({ticketConversations.length} ticket
              {ticketConversations.length !== 1 ? "s" : ""})
            </span>
          )}
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-400 hover:text-gray-200 transition-colors"
          title={isExpanded ? "Minimize" : "Maximize"}
        >
          {isExpanded ? (
            <Minimize2 className="h-4 w-4" />
          ) : (
            <Maximize2 className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Terminal Body */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-900 font-mono text-sm">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <Loader className="h-6 w-6 animate-spin text-blue-400" />
            <span className="ml-2 text-gray-400">Loading conversation...</span>
          </div>
        )}

        {error && (
          <div className="flex items-center text-red-400 bg-red-900/20 p-3 rounded">
            <AlertCircle className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && ticketConversations.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            <Terminal className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No LLM conversations yet.</p>
            <p className="text-xs mt-1">
              Messages will appear here as agents interact with the LLM.
            </p>
          </div>
        )}

        {!loading && !error && ticketConversations.length > 0 && (
          <div className="space-y-4">
            {ticketConversations.map((ticketConv) => {
              const totalEntries =
                ticketConv.nonTaskEntries.length +
                ticketConv.tasks.reduce(
                  (sum, task) => sum + task.entries.length,
                  0
                );

              return (
                <div
                  key={ticketConv.ticketKey}
                  className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800/50"
                >
                  {/* Phase 8: Ticket Block Header */}
                  <button
                    onClick={() => toggleTicketExpansion(ticketConv.ticketKey)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-800 hover:bg-gray-750 transition-colors text-left"
                  >
                    <div className="flex items-center space-x-3">
                      {ticketConv.isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )}
                      <div>
                        <div className="text-sm font-semibold text-green-400">
                          {ticketConv.ticketKey}
                        </div>
                        <div className="text-xs text-gray-500">
                          {ticketConv.tasks.length} task
                          {ticketConv.tasks.length !== 1 ? "s" : ""},{" "}
                          {totalEntries} message{totalEntries !== 1 ? "s" : ""}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {ticketConv.isExpanded ? "Collapse" : "Expand"}
                    </div>
                  </button>

                  {/* Ticket Conversation Content */}
                  {ticketConv.isExpanded && (
                    <div className="p-4 max-h-96 overflow-y-auto space-y-3">
                      {/* Non-task entries (Task Generation) - Only show for actual tickets */}
                      {ticketConv.nonTaskEntries.length > 0 &&
                        isActualTicket(ticketConv.ticketKey) && (
                          <div className="border border-gray-600 rounded-lg overflow-hidden bg-gray-900/30">
                            {/* Task Generation Header - Collapsible */}
                            <button
                              onClick={() =>
                                toggleTaskGenerationExpansion(
                                  ticketConv.ticketKey
                                )
                              }
                              className="w-full flex items-center justify-between px-3 py-2 bg-gray-700/50 hover:bg-gray-700 transition-colors text-left"
                            >
                              <div className="flex items-center space-x-2">
                                {ticketConv.isTaskGenerationExpanded ? (
                                  <ChevronDown className="h-3 w-3 text-gray-400" />
                                ) : (
                                  <ChevronRight className="h-3 w-3 text-gray-400" />
                                )}
                                <div className="text-xs">
                                  <span className="font-semibold text-purple-400">
                                    Task Generation
                                  </span>
                                  <span className="text-gray-400 ml-2">
                                    ({ticketConv.nonTaskEntries.length} message
                                    {ticketConv.nonTaskEntries.length !== 1
                                      ? "s"
                                      : ""}
                                    )
                                  </span>
                                </div>
                              </div>
                              <div className="text-xs text-gray-500">
                                {ticketConv.nonTaskEntries.length} message
                                {ticketConv.nonTaskEntries.length !== 1
                                  ? "s"
                                  : ""}
                              </div>
                            </button>

                            {/* Task Generation Content */}
                            {ticketConv.isTaskGenerationExpanded && (
                              <div className="p-3 space-y-3">
                                {ticketConv.nonTaskEntries.map((entry) =>
                                  renderEntry(entry)
                                )}
                              </div>
                            )}
                          </div>
                        )}

                      {/* For non-ticket groups (agent names), show entries without "Task Generation" label */}
                      {ticketConv.nonTaskEntries.length > 0 &&
                        !isActualTicket(ticketConv.ticketKey) && (
                          <div className="space-y-3">
                            {ticketConv.nonTaskEntries.map((entry) =>
                              renderEntry(entry)
                            )}
                          </div>
                        )}

                      {/* Task blocks (SWRM-96: includes [No Active Task] groups) */}
                      {ticketConv.tasks.map((task) => {
                        const isNoActiveTask = task.taskId === null;
                        const taskKey = task.taskId || task.taskTitle; // Use title as key for null taskId

                        return (
                          <div
                            key={taskKey}
                            className="border border-gray-600 rounded-lg overflow-hidden bg-gray-900/30"
                          >
                            {/* Task Header */}
                            <button
                              onClick={() =>
                                toggleTaskExpansion(
                                  ticketConv.ticketKey,
                                  taskKey
                                )
                              }
                              className="w-full flex items-center justify-between px-3 py-2 bg-gray-700/50 hover:bg-gray-700 transition-colors text-left"
                            >
                              <div className="flex items-center space-x-2">
                                {task.isExpanded ? (
                                  <ChevronDown className="h-3 w-3 text-gray-400" />
                                ) : (
                                  <ChevronRight className="h-3 w-3 text-gray-400" />
                                )}
                                <div className="text-xs">
                                  {isNoActiveTask ? (
                                    // [No Active Task] styling
                                    <span className="font-semibold text-gray-400">
                                      {task.taskTitle}
                                    </span>
                                  ) : (
                                    // Regular task styling
                                    <>
                                      <span className="font-mono font-semibold text-blue-400">
                                        {task.taskId}
                                      </span>
                                      <span className="text-gray-300 ml-2">
                                        - {task.taskTitle}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="text-xs text-gray-500">
                                {task.entries.length} message
                                {task.entries.length !== 1 ? "s" : ""}
                              </div>
                            </button>

                            {/* Task Conversation Content */}
                            {task.isExpanded && (
                              <div className="p-3 space-y-3">
                                {task.entries.map((entry) =>
                                  renderEntry(entry)
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Terminal Footer */}
      <div
        className={`px-4 py-2 bg-gray-800 ${
          isExpanded ? "" : "rounded-b-lg"
        } border-t border-gray-700 text-xs text-gray-400`}
      >
        <div className="flex items-center justify-between">
          <span>Workflow ID: {workflowId}</span>
          <span className="flex items-center space-x-1">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            <span>Live</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default LLMConversationTerminal;
